import { CalendarRange, ListChecks, Server, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusChip } from '@/components/ui/status-chip';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import { cn } from '@/lib/utils';
import {
  RiskSelectionMode,
  useRiskSelection,
} from '@/sections/export/hooks/useRiskSelection';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';

interface RiskSelectionStepProps {
  /**
   * The hook instance, owned by the parent wizard so `resolvedRiskKeys` is
   * available at submit time.
   */
  selection: ReturnType<typeof useRiskSelection>;
}

const MODE_OPTIONS: ReadonlyArray<{
  value: RiskSelectionMode;
  label: string;
  sublabel: string;
  icon: typeof ShieldCheck;
}> = [
  {
    value: 'all',
    label: 'All open risks',
    sublabel: 'Cover every currently open risk in this export.',
    icon: ShieldCheck,
  },
  {
    value: 'dateRange',
    label: 'By date range',
    sublabel: 'Cover open risks discovered or last seen within a window.',
    icon: CalendarRange,
  },
  {
    value: 'byAsset',
    label: 'By asset',
    sublabel: 'Cover the open risks found on selected assets.',
    icon: Server,
  },
  {
    value: 'specific',
    label: 'Specific vulnerabilities',
    sublabel: 'Hand-pick the exact risks this export should cover.',
    icon: ListChecks,
  },
];

/** StatusChip pill for a risk's status code (e.g. `OC` → "Open"). */
function RiskStatusChip({ status }: { status: string }) {
  const { statusLabel } = getRiskStatusLabel(status);
  const label = statusLabel || 'Unknown';
  const variant = getVariantForStatus(label, vulnerabilityStatusVariants);
  return <StatusChip label={label} variant={variant} />;
}

/**
 * Screen 1 of the risk-selection flow: the mode picker. Renders the radiogroup
 * of the three scope options and nothing else. Choosing `all` skips the config
 * screen entirely (the parent wizard drops `scopeConfig` from its step list).
 */
export function RiskModeStep({ selection }: RiskSelectionStepProps) {
  const { mode, setMode } = selection;

  return (
    <div
      role="radiogroup"
      aria-label="Which risks should this export cover?"
      className="flex flex-col gap-3"
    >
      {MODE_OPTIONS.map(opt => {
        const isSelected = mode === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => setMode(opt.value)}
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
              'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
              isSelected
                ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                : 'border-chariot-stroke hover:bg-chariot-background-secondary'
            )}
          >
            <Icon
              className={cn(
                'mt-0.5 size-5 shrink-0',
                isSelected
                  ? 'text-chariot-brand-primary'
                  : 'text-chariot-text-secondary'
              )}
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-chariot-text-primary text-sm font-medium">
                {opt.label}
              </span>
              <span className="text-chariot-text-secondary text-xs">
                {opt.sublabel}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Screen 2 of the risk-selection flow: the config for the chosen mode. Renders
 * the date-range block (`dateRange`) or the specific-risk picker (`specific`).
 * For `all` it renders nothing — that screen is skipped by the parent wizard,
 * so this branch is never shown.
 */
export function RiskScopeConfigStep({ selection }: RiskSelectionStepProps) {
  const {
    mode,
    dateField,
    setDateField,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedKeys,
    setSelectedKeys,
    selectedSpecificTags,
    setSelectedSpecificTags,
    selectedAssetKeys,
    setSelectedAssetKeys,
    assetSearch,
    setAssetSearch,
    resolvedRiskKeys,
    isResolving,
    riskOptions,
    isLoadingOptions,
    assetOptions,
    isLoadingAssetOptions,
  } = selection;

  // Tag options for the "specific" tag multi-select — same source the
  // Vulnerabilities page tag filter uses, so `filterInternalTags` masking (hides
  // hunt/agent/cato provenance tags from non-Praetorians) is applied for free.
  // Only fetched when the picker is on screen ('specific' mode).
  const { data: vulnFilters, status: vulnFiltersStatus } =
    useGetVulnerabilityFilters({ enabled: mode === 'specific' });
  const tagFilterOptions: (FilterOption | GroupOption)[] =
    vulnFilters?.tagFilters ?? [];

  // Local search for the "specific" risk picker (client-side filter over the
  // fully-paged open-risk list). The "byAsset" picker uses server-side search
  // via `assetSearch`/`setAssetSearch` instead — see useRiskSelection.
  const [search, setSearch] = useState('');

  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? riskOptions.filter(
        opt =>
          opt.label.toLowerCase().includes(normalizedSearch) ||
          opt.key.toLowerCase().includes(normalizedSearch)
      )
    : riskOptions;

  const toggleKey = (key: string) => {
    setSelectedKeys(
      selectedKeys.includes(key)
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key]
    );
  };

  const toggleAssetKey = (key: string) => {
    setSelectedAssetKeys(
      selectedAssetKeys.includes(key)
        ? selectedAssetKeys.filter(k => k !== key)
        : [...selectedAssetKeys, key]
    );
  };

  // "Select all N" — unions the currently-visible risk keys into any prior
  // hand-picks (a user with picks from a previous filter shouldn't lose them).
  // `filteredOptions` already respects both the server-side tag filter (which
  // narrows `riskOptions` via `useGetRisks`) and the client-side text search,
  // so N reflects both filters.
  //
  // The button is additionally disabled while the tag-scoped query is loading
  // (isLoadingOptions) — otherwise a fast click during a tag change could act
  // on stale rows and union previous-tag keys into the selection.
  const filteredKeys = filteredOptions.map(o => o.key);
  const allFilteredSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every(k => selectedKeys.includes(k));
  const canSelectAll =
    filteredKeys.length > 0 && !allFilteredSelected && !isLoadingOptions;
  const selectAllFiltered = () => {
    setSelectedKeys(Array.from(new Set([...selectedKeys, ...filteredKeys])));
  };
  const clearSelection = () => {
    setSelectedKeys([]);
  };

  const matchedCount = resolvedRiskKeys?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {mode === 'dateRange' && (
        <div className="border-chariot-stroke flex flex-col gap-4 rounded-lg border p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="risk-date-field">Date field</Label>
            <Select
              value={dateField}
              onValueChange={value =>
                setDateField(value as 'created' | 'visited')
              }
            >
              <SelectTrigger id="risk-date-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created">Created (first seen)</SelectItem>
                <SelectItem value="visited">Last seen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="risk-start-date">Start date</Label>
              <Input
                id="risk-start-date"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="risk-end-date">End date</Label>
              <Input
                id="risk-end-date"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <p className="text-chariot-text-secondary text-xs" aria-live="polite">
            {!startDate || !endDate
              ? 'Pick a start and end date to count matching risks.'
              : isResolving
                ? 'Counting matching risks…'
                : `${matchedCount} open risk${matchedCount === 1 ? ' matches' : 's match'} this range.`}
          </p>
        </div>
      )}

      {mode === 'byAsset' && (
        <div className="border-chariot-stroke flex flex-col gap-3 rounded-lg border p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="asset-search">Search assets</Label>
            <Input
              id="asset-search"
              type="text"
              placeholder="Search by name, identifier, or DNS…"
              value={assetSearch}
              onChange={e => setAssetSearch(e.target.value)}
            />
          </div>

          <ScrollArea className="border-chariot-stroke h-64 rounded-md border">
            <ul className="flex flex-col p-1">
              {isLoadingAssetOptions ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  Loading assets…
                </li>
              ) : assetOptions.length === 0 ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  {assetSearch.trim()
                    ? 'No assets match your search.'
                    : 'No assets found.'}
                </li>
              ) : (
                assetOptions.map(opt => {
                  const checkboxId = `asset-option-${opt.key}`;
                  const isChecked = selectedAssetKeys.includes(opt.key);
                  return (
                    <li key={opt.key}>
                      <label
                        htmlFor={checkboxId}
                        className="hover:bg-chariot-background-secondary flex cursor-pointer items-center gap-3 rounded-xs px-2 py-2"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isChecked}
                          onCheckedChange={() => toggleAssetKey(opt.key)}
                        />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="text-chariot-text-primary truncate text-sm">
                            {opt.label}
                          </span>
                          {opt.detail && opt.detail !== opt.label && (
                            <span className="text-chariot-text-secondary truncate text-xs">
                              {opt.detail}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
          </ScrollArea>

          <p className="text-chariot-text-secondary text-xs" aria-live="polite">
            {selectedAssetKeys.length === 0
              ? 'Select at least one asset to scope this export.'
              : isResolving
                ? `${selectedAssetKeys.length} asset${selectedAssetKeys.length === 1 ? '' : 's'} selected — counting matching risks…`
                : `${selectedAssetKeys.length} asset${selectedAssetKeys.length === 1 ? '' : 's'} selected — ${matchedCount} open risk${matchedCount === 1 ? '' : 's'} covered.`}
          </p>
        </div>
      )}

      {mode === 'specific' && (
        <div className="border-chariot-stroke flex flex-col gap-3 rounded-lg border p-4">
          {/*
           * Server-side tag filter. Narrows the picker's open-risk list via
           * `advancedFilters.tags` on `useGetRisks` (see useRiskSelection).
           * Changing tags does NOT clear `selectedKeys` — mirrors the local
           * text-search behavior; hand-picks are only removed when the user
           * unchecks them.
           */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="risk-tag-filter">Filter by tags</Label>
            <div>
              <FilterDropdown
                id="risk-tag-filter"
                label="Filter by tags"
                options={tagFilterOptions}
                selectedValues={selectedSpecificTags}
                isLoadingOptions={vulnFiltersStatus === 'pending'}
                showSearch={false}
                onApply={opts =>
                  setSelectedSpecificTags(opts.map(o => String(o.value)))
                }
                onClear={() => setSelectedSpecificTags([])}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="risk-search">Search open risks</Label>
            <Input
              id="risk-search"
              type="text"
              placeholder="Filter by name or key…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/*
           * Bulk-selection toolbar. "Select all N" unions filtered keys into
           * `selectedKeys` (preserving prior picks); "Clear selection" resets
           * to [] and only renders when there's something to clear.
           */}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              disabled={!canSelectAll}
              onClick={selectAllFiltered}
              data-testid="risk-select-all-button"
            >
              Select all {filteredOptions.length}
            </Button>
            {selectedKeys.length > 0 && (
              <>
                <span
                  aria-hidden
                  className="text-chariot-text-disabled text-xs"
                >
                  |
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={clearSelection}
                  data-testid="risk-clear-selection-button"
                >
                  Clear selection
                </Button>
              </>
            )}
          </div>

          <ScrollArea className="border-chariot-stroke h-64 rounded-md border">
            <ul className="flex flex-col p-1">
              {isLoadingOptions ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  Loading open risks…
                </li>
              ) : filteredOptions.length === 0 ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  {riskOptions.length === 0
                    ? 'No open risks found.'
                    : 'No risks match your search.'}
                </li>
              ) : (
                filteredOptions.map(opt => {
                  const checkboxId = `risk-option-${opt.key}`;
                  const isChecked = selectedKeys.includes(opt.key);
                  return (
                    <li key={opt.key}>
                      <label
                        htmlFor={checkboxId}
                        className="hover:bg-chariot-background-secondary flex cursor-pointer items-center gap-3 rounded-xs px-2 py-2"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isChecked}
                          onCheckedChange={() => toggleKey(opt.key)}
                        />
                        <span className="text-chariot-text-primary min-w-0 flex-1 truncate text-sm">
                          {opt.label}
                        </span>
                        <RiskStatusChip status={opt.status} />
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
          </ScrollArea>

          <p className="text-chariot-text-secondary text-xs" aria-live="polite">
            {(() => {
              // Precedence mirrors `resolvedRiskKeys`: hand-picks first, else
              // tag filter narrows the scope, else all open risks.
              if (selectedKeys.length > 0) {
                return `${selectedKeys.length} risk${selectedKeys.length === 1 ? '' : 's'} selected.`;
              }
              if (selectedSpecificTags.length > 0) {
                if (isResolving) {
                  return 'Counting matching risks…';
                }
                const matched = resolvedRiskKeys?.length ?? 0;
                // Zero-match: don't let the user think an empty tag filter
                // means "all open risks". The backend would silently interpret
                // risk_keys: [] that way, so the wizard is gated
                // (isSpecificScopeReady=false); tell the user why.
                if (matched === 0) {
                  return 'No open risks match the current tag filter — pick a different tag or clear the filter.';
                }
                return `No risks selected — the export will cover all open risks matching the tag filter (${matched} risk${matched === 1 ? '' : 's'}).`;
              }
              return 'No risks selected — the export will cover all open risks.';
            })()}
          </p>
        </div>
      )}
    </div>
  );
}
